import { useRef, useState } from "react";
import { Check, ImagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { mediaUrl } from "@/components/chatStyles";

const API = process.env.REACT_APP_BACKEND_URL;
export const inp = "w-full bg-white border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-accent";
export const pill = "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold";

export const Field = ({ label, hint, children, className = "" }) => (
  <label className={`block text-xs text-muted-foreground ${className}`}><span className="uppercase tracking-widest">{label}</span>{children}{hint && <span className="block mt-1 text-[10px] normal-case tracking-normal">{hint}</span>}</label>
);

export const Text = ({ k, d, set, label, hint, rows, placeholder, max }) => (
  <Field label={label} hint={hint}>
    {rows ? <textarea data-testid={`cb-${k}`} rows={rows} maxLength={max} value={d[k] ?? ""} onChange={(e) => set(k, e.target.value)} placeholder={placeholder} className={`${inp} mt-1`} />
      : <input data-testid={`cb-${k}`} maxLength={max} value={d[k] ?? ""} onChange={(e) => set(k, e.target.value)} placeholder={placeholder} className={`${inp} mt-1`} />}
  </Field>
);

export const Num = ({ k, d, set, label, hint, min, max, step = 1 }) => (
  <Field label={label} hint={hint}><input data-testid={`cb-${k}`} type="number" min={min} max={max} step={step} value={d[k] ?? ""} onChange={(e) => set(k, Number(e.target.value))} className={`${inp} mt-1`} /></Field>
);

export const Range = ({ k, d, set, label, min, max, step, fmt = (v) => v }) => (
  <Field label={`${label} · ${fmt(d[k])}`}><input data-testid={`cb-${k}`} type="range" min={min} max={max} step={step} value={d[k] ?? min} onChange={(e) => set(k, Number(e.target.value))} className="w-full mt-3 accent-[#C86A51]" /></Field>
);

export const Toggle = ({ k, d, set, label, on = "On", off = "Off", hint }) => (
  <div className="flex items-start justify-between gap-3 rounded-xl border border-border bg-white px-3 py-2.5" data-testid={`cb-${k}-row`}>
    <div><p className="text-sm font-medium">{label}</p><p className="text-[11px] text-muted-foreground">{d[k] !== false && d[k] != null ? on : off}{hint ? ` · ${hint}` : ""}</p></div>
    <Switch data-testid={`cb-${k}`} checked={d[k] !== false && d[k] != null} onCheckedChange={(v) => set(k, v)} />
  </div>
);

/* Segmented choice (2–6 options) or option cards when `cards` */
export const Choice = ({ k, d, set, label, options, hint }) => (
  <Field label={label} hint={hint}>
    <div className="mt-1.5 flex flex-wrap gap-1.5 normal-case tracking-normal" data-testid={`cb-${k}`}>
      {options.map(([v, l]) => <button key={v} type="button" data-testid={`cb-${k}-${v}`} data-selected={String(d[k]) === String(v) ? "1" : "0"} onClick={() => set(k, v)} className={`${pill} border ${String(d[k]) === String(v) ? "bg-[#1A2E26] text-white border-transparent" : "bg-white border-border text-foreground hover:border-[#1A2E26]/40"}`}>{String(d[k]) === String(v) && <Check size={11} />}{l}</button>)}
    </div>
  </Field>
);

export const Color = ({ label, value, onChange, testId }) => (
  <div className="flex items-center gap-2 rounded-lg border border-border bg-white px-2 py-1.5" data-testid={testId}>
    <input type="color" value={value || "#888888"} onChange={(e) => onChange(e.target.value)} className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent p-0" aria-label={label} />
    <span className="flex-1 min-w-0 text-xs truncate">{label}<span className="block text-[10px] text-muted-foreground">{value || "theme default"}</span></span>
    {value && <button type="button" onClick={() => onChange("")} className="text-[10px] text-muted-foreground underline">reset</button>}
  </div>
);

/* Image upload → object storage; stores the relative /api/media/file/... URL */
export const ImageUpload = ({ k, d, set, label, hint, round }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const up = async (f) => {
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error("Image must be 3MB or smaller");
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); const r = await fetch(`${API}/api/media/upload`, { method: "POST", body: fd }); const j = await r.json(); if (!r.ok) throw new Error(j.detail || "Upload failed"); set(k, j.url); toast.success("Image uploaded — check the preview"); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="rounded-xl border border-border bg-white p-3 flex items-center gap-3" data-testid={`cb-${k}-upload`}>
      <span className={`grid place-items-center w-14 h-14 shrink-0 overflow-hidden bg-[#F6F3EA] border border-border ${round ? "rounded-full" : "rounded-xl"}`}>{d[k] ? <img src={mediaUrl(d[k])} alt="" className="w-full h-full object-cover" /> : <ImagePlus size={18} className="text-muted-foreground" />}</span>
      <div className="min-w-0 flex-1"><p className="text-sm font-medium">{label}</p>{hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          <button type="button" data-testid={`cb-${k}-pick`} disabled={busy} onClick={() => ref.current?.click()} className={`${pill} border border-border bg-white disabled:opacity-50`}><ImagePlus size={12} /> {busy ? "Uploading…" : d[k] ? "Change image" : "Upload image"}</button>
          {d[k] && <button type="button" data-testid={`cb-${k}-clear`} onClick={() => set(k, "")} className={`${pill} border border-rose-200 bg-rose-50 text-rose-700`}><Trash2 size={12} /> Remove</button>}
        </div>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => up(e.target.files?.[0])} />
      </div>
    </div>
  );
};
